/**
 * Schema entries for the "Schedule details" section of the Details step.
 *
 * Covers: calendar row → duration → buffer → find time → date/time picker
 * (manual/adhoc) → calendar visibility → location → HackerRank template →
 * candidate phone → host → office → note taker.
 *
 * Pure builder function — receives all dependencies as args, returns
 * schema entries. No React hooks called here.
 */
import { ScheduleCalendarRowField } from '../../fields/ScheduleCalendarRowField';
import { CandidatePhoneField } from '../../fields/CandidatePhoneField';
import { DateTimeField, parse12hrTime } from '../../fields/dateTime';
import { BufferField, FindTimeField } from '../../../../calendar/selfSchedule';
import { copy, isDateTimeEditingCondition } from './constants';
import type { ScheduleCalendarData } from '../useScheduleCalendarData';
import type { FindTimeFieldBag, BufferFieldBag } from './types';

interface ScheduleDetailsSchemaDeps {
  isManualOrAdhoc: boolean;
  isSelfSchedule: boolean;
  apiReference: string;
  calendarData: ScheduleCalendarData;
  shouldShowCalendarRow: boolean;
  durationField: Record<string, unknown>;
  locationField: Record<string, unknown>;
  hostField: Record<string, unknown>;
  officeField: Record<string, unknown>;
  noteTakerField: Record<string, unknown>;
  hackerRankTemplateOptions: Array<{ value: string; label: string }>;
  hrLoading: boolean;
  startTimeOptions: Array<{ value: string; label: string }>;
  endTimeOptions: Array<{ value: string; label: string }>;
  datePickerMinDate: Date;
  candidateName?: string;
  candidateAvatar?: string;
  candidatePhone?: string;
  findTimeProps?: FindTimeFieldBag;
  bufferProps?: BufferFieldBag;
}

export function buildScheduleDetailsSchema({
  isManualOrAdhoc,
  isSelfSchedule,
  apiReference,
  calendarData,
  shouldShowCalendarRow,
  durationField,
  locationField,
  hostField,
  officeField,
  noteTakerField,
  hackerRankTemplateOptions,
  hrLoading,
  startTimeOptions,
  endTimeOptions,
  datePickerMinDate,
  candidateName,
  candidateAvatar,
  candidatePhone,
  findTimeProps,
  bufferProps,
}: ScheduleDetailsSchemaDeps): unknown[] {
  return [
    {
      type: 'sectionLabel',
      label: copy.scheduleSectionLabel,
    },

    // Calendar row — first field under "Schedule details".
    // Two FormBuilder entries share the `isCalendarEditing` toggle:
    //   - Custom read-only strip (view mode)
    //   - Native select (edit mode, writes structured `calendar_info`)
    ...(shouldShowCalendarRow
      ? [
          {
            type: 'input' as const,
            name: 'isCalendarEditing',
            hidden: true,
          },
          {
            type: 'custom' as const,
            name: 'calendar_info',
            condition: {
              operator: 'notEqual' as const,
              field: 'isCalendarEditing',
              value: 'edit',
              preserveValue: true,
            },
            render: ({
              field,
              formMethods,
            }: {
              field: { value: unknown };
              formMethods: {
                setValue: (_n: string, _v: unknown, _o?: unknown) => void;
                watch: (_n: string) => unknown;
              };
            }) => {
              const info = field.value as
                | { provider_calendar_id?: string }
                | null
                | undefined;
              return (
                <ScheduleCalendarRowField
                  summaryName={calendarData.summaryName}
                  showYouSuffix={calendarData.showYouSuffix}
                  isLoading={calendarData.isLoading}
                  hideEdit={calendarData.hideEdit}
                  disabledTooltip={calendarData.disabledTooltip}
                  isCalConnected={calendarData.isCalConnected}
                  calendarInfoId={info?.provider_calendar_id}
                  calendars={calendarData.calendars}
                  defaultCalendar={calendarData.defaultCalendar}
                  isEdit={apiReference === 'edit'}
                  refetchUuid={calendarData.refetchUuid}
                  formMethods={formMethods}
                />
              );
            },
          },
          {
            type: 'select' as const,
            name: 'calendar_info_picker',
            label: copy.calendar.label,
            options: calendarData.selectOptions,
            condition: {
              operator: 'equal' as const,
              field: 'isCalendarEditing',
              value: 'edit',
              preserveValue: true,
            },
            fieldProps: {
              loading: calendarData.isLoading,
              searchable: false,
            },
            onChange: (
              value: unknown,
              formMethods: {
                setValue: (_n: string, _v: unknown, _o?: unknown) => void;
              }
            ) => {
              const id = Array.isArray(value)
                ? value[0]
                : (value as string | undefined);
              if (typeof id !== 'string' || !id) return;
              const cal = calendarData.findById(id);
              formMethods.setValue('calendar_info', {
                provider_calendar_id: id,
                is_primary: Boolean(cal?.is_primary),
              });
            },
          },
        ]
      : []),

    {
      ...durationField,
      required: true,
      validate: (value: unknown) =>
        typeof value === 'number' && value > 0
          ? true
          : 'Duration must be greater than 0',
    },

    // Buffer time — self-schedule only, behind feInterviewBuffer flag.
    ...(isSelfSchedule && bufferProps
      ? [
          {
            type: 'custom' as const,
            name: 'buffer',
            render: () => (
              <BufferField
                value={bufferProps.value}
                onChange={bufferProps.onChange}
              />
            ),
          },
        ]
      : []),

    // Find Time — self-schedule only. Hidden until an interviewer is picked.
    ...(isSelfSchedule && findTimeProps
      ? [
          {
            type: 'custom' as const,
            name: 'findTime',
            render: ({
              formMethods,
            }: {
              formMethods: { watch: (_n: string) => unknown };
            }) => {
              const raw = formMethods.watch('participant_ids');
              const ids: Array<string | number> = Array.isArray(raw)
                ? (raw as Array<string | number>)
                : raw
                  ? [raw as string | number]
                  : [];
              if (ids.length === 0) return null;
              return (
                <FindTimeField
                  value={findTimeProps.value}
                  slotsCount={findTimeProps.slotsCount}
                  timezone={findTimeProps.timezone}
                  minimumNoticeHours={findTimeProps.minimumNoticeHours}
                  onApply={findTimeProps.onApply}
                  onClear={findTimeProps.onClear}
                  onPreview={findTimeProps.onPreview}
                />
              );
            },
          },
        ]
      : []),

    // Date/Time picker — manual/adhoc only.
    // `isDateTimeEditing` hidden field toggles between the read-only
    // strip and the date + time selects.
    ...(isManualOrAdhoc
      ? [
          {
            type: 'input' as const,
            name: 'isDateTimeEditing',
            hidden: true,
          },
          {
            type: 'custom' as const,
            name: 'dateTime',
            render: ({
              field,
              formMethods,
            }: {
              field: { value: unknown };
              formMethods: {
                setValue: (_n: string, _v: unknown) => void;
                watch: (_n: string) => unknown;
              };
            }) => {
              const mode = formMethods.watch('isDateTimeEditing');
              if (mode === 'edit') return null;
              return (
                <DateTimeField
                  value={typeof field.value === 'string' ? field.value : ''}
                  onEdit={() =>
                    formMethods.setValue('isDateTimeEditing', 'edit')
                  }
                />
              );
            },
          },
          {
            type: 'date' as const,
            name: 'startDate',
            label: 'Start date',
            required: 'Start date is required',
            condition: isDateTimeEditingCondition,
            fieldProps: {
              mode: 'single',
              triggerWidth: '100%',
              calendarProps: { minDate: datePickerMinDate },
            },
          },
          {
            type: 'section' as const,
            columns: 2,
            condition: isDateTimeEditingCondition,
            fields: [
              {
                type: 'select' as const,
                name: 'startTime',
                label: 'Start time',
                required: 'Start time is required',
                options: startTimeOptions,
                colSpan: 1,
                fieldProps: { searchable: true },
              },
              {
                type: 'select' as const,
                name: 'endTime',
                label: 'End time',
                required: 'End time is required',
                options: endTimeOptions,
                colSpan: 1,
                fieldProps: { searchable: true },
                validate: (
                  value: unknown,
                  allValues: Record<string, unknown>
                ) => {
                  if (!value || !allValues.startTime) return true;
                  const start = parse12hrTime(allValues.startTime as string);
                  const end = parse12hrTime(value as string);
                  if (!start || !end) return true;
                  const startMin = start.hours * 60 + start.minutes;
                  const endMin = end.hours * 60 + end.minutes;
                  if (endMin <= startMin)
                    return 'End time must be after start time';
                  return true;
                },
              },
            ],
          },
        ]
      : []),

    {
      type: 'switch' as const,
      name: 'calendar_visibility',
      fieldProps: {
        switchLabel: copy.calendarVisibility.label,
        description: copy.calendarVisibility.subLabel,
        fullWidth: true,
        labelAlign: 'center',
        labelPosition: 'left',
      },
    },

    locationField,

    {
      type: 'select' as const,
      name: 'ext_interview_template_id',
      label: 'HackerRank Template',
      placeholder: 'Select template',
      options: hackerRankTemplateOptions,
      loading: hrLoading,
      condition: {
        operator: 'equal',
        field: 'location',
        value: 'hackerrank',
      },
    },

    // Candidate phone — always mount the block; visibility is gated by
    // the `location === 'phone_interview'` condition on each row. The
    // `isCandidatePhoneEditing` hidden toggle switches between the
    // read-only display and the phone input.
    ...[
      {
        type: 'input' as const,
        name: 'isCandidatePhoneEditing',
        hidden: true,
      },
      {
        type: 'custom' as const,
        name: 'candidatePhoneDisplay',
        condition: {
          operator: 'and' as const,
          conditions: [
            {
              operator: 'equal' as const,
              field: 'location',
              value: 'phone_interview',
            },
            {
              operator: 'notEqual' as const,
              field: 'isCandidatePhoneEditing',
              value: 'edit',
            },
          ],
        },
        render: ({
          formMethods,
        }: {
          formMethods: {
            setValue: (_n: string, _v: unknown) => void;
            watch: (_n: string) => unknown;
          };
        }) => (
          <CandidatePhoneField
            candidateName={candidateName}
            candidateAvatar={candidateAvatar}
            phoneNumber={candidatePhone}
            onEdit={() =>
              formMethods.setValue('isCandidatePhoneEditing', 'edit')
            }
          />
        ),
      },
      {
        type: 'phone' as const,
        name: 'candidate_phone',
        label: 'Candidate phone',
        required: 'Candidate phone is required',
        validate: (value: unknown) => {
          const v = value as { dialCode?: string; number?: string } | undefined;
          const num = v?.number?.trim() ?? '';
          if (!num) return 'Candidate phone is required';
          return true;
        },
        condition: {
          operator: 'and' as const,
          conditions: [
            {
              operator: 'equal' as const,
              field: 'location',
              value: 'phone_interview',
            },
            {
              operator: 'equal' as const,
              field: 'isCandidatePhoneEditing',
              value: 'edit',
            },
          ],
        },
        fieldProps: { defaultCountry: 'US' },
      },
    ],

    hostField,
    officeField,

    {
      // Note-taker only appears when an interviewer is picked AND the
      // location supports it (zoom / google_meet / teams).
      ...noteTakerField,
      condition: {
        operator: 'and',
        conditions: [
          { operator: 'isNotEmpty', field: 'participant_ids' },
          {
            operator: 'in',
            field: 'location',
            value: ['zoom', 'google_meet', 'teams'],
          },
        ],
      },
    },
  ];
}
